import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from "@angular/core";
import ISkillsKeyedByType from "@skills-app/models/skillsKeyedByType";
import { ISkillNode } from "@skills-app/models/skillNode";

type SkillBucket = ISkillNode[] | string[];

type SkillsByTypeLookup = {
  [skillTypeName: string]: SkillBucket | unknown;
  skillTypeNames?: string[];
};

@Component({
  standalone: false,
  selector: "app-skill-list",
  templateUrl: "./skill-list.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./skill-list.component.css"],
})
export class SkillListComponent implements OnChanges {
  @Input() skillsByType: ISkillsKeyedByType | null = null;
  @Input() selectedSkillRelatedSkills: ISkillNode[] = [];
  @Input() thingspaceDraggableList = false;

  @Output() skillSelected = new EventEmitter<ISkillNode>();

  public selectedSkill: ISkillNode | null = null;

  // Cached lookups to avoid repeated heavy work during change detection
  private skillTypeNamesCache: string[] = [];
  private skillsForTypeCache: Record<string, ISkillNode[]> = {};

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["skillsByType"]) {
      this.rebuildCache();
    }
  }

  private rebuildCache(): void {
    const sbt = this.skillsByType as unknown as SkillsByTypeLookup | null;

    const names = sbt?.skillTypeNames;
    this.skillTypeNamesCache = Array.isArray(names) ? names : [];

    const map: Record<string, ISkillNode[]> = {};
    for (const typeName of this.skillTypeNamesCache) {
      const value = sbt ? sbt[typeName] : undefined;
      map[typeName] = this.isSkillNodeArray(value) ? value : [];
    }

    this.skillsForTypeCache = map;
  }

  selectSkill(skill: ISkillNode): void {
    this.selectedSkill = skill;
    this.cdr.detectChanges();
    this.skillSelected.emit(skill);
  }

  isSelected(skill: ISkillNode): boolean {
    return !!this.selectedSkill?.Id && this.selectedSkill.Id === skill.Id;
  }

  isSkillRelated(
    skill: ISkillNode,
    selectedSkillRelatedSkills: ISkillNode[],
  ): boolean {
    return (selectedSkillRelatedSkills ?? []).some((x) => x.Id == skill.Id);
  }

  private isSkillNodeArray(value: unknown): value is ISkillNode[] {
    return (
      Array.isArray(value) &&
      value.every(
        (v) =>
          typeof v === "object" &&
          v !== null &&
          "Id" in (v as Record<string, unknown>) &&
          "Name" in (v as Record<string, unknown>),
      )
    );
  }

  /**
   * Template-safe accessor:
   * Returns cached values so template calls stay cheap.
   */
  getSkillsForType(skillTypeName: string): ISkillNode[] {
    return this.skillsForTypeCache[skillTypeName] ?? [];
  }

  getSkillTypeNames(): string[] {
    return this.skillTypeNamesCache;
  }

  trackBySkillTypeName(index: number, name: string): string | number {
    return name ?? index;
  }

  trackBySkill(index: number, skill: ISkillNode): string | number {
    return skill?.Id ?? index;
  }
}
